/**
 * How long a lead has been waiting: when it came in, how long nobody has
 * talked to it, and the 5-minute call race of a brand new lead. Pure (vitest).
 */

/** Seconds the team has to call a new form lead. */
export const CALL_RACE_SECONDS = 5 * 60;

/** New leads stop popping up after this long (they stay on the board). */
export const ALERT_MAX_MINUTES = 60;

const pad = (n: number) => String(n).padStart(2, "0");

const MONTHS = [
  "ene",
  "feb",
  "mar",
  "abr",
  "may",
  "jun",
  "jul",
  "ago",
  "sep",
  "oct",
  "nov",
  "dic",
];

const dayStart = (time: number) => {
  const date = new Date(time);
  date.setHours(0, 0, 0, 0);
  return date.getTime();
};

/** "hoy 10:42", "ayer 18:05" or "3 nov 09:12". */
export const entryLabel = (createdAt: string, now: number): string => {
  const date = new Date(createdAt);
  const time = `${pad(date.getHours())}:${pad(date.getMinutes())}`;
  const days = Math.round((dayStart(now) - dayStart(date.getTime())) / 864e5);
  if (days <= 0) return `hoy ${time}`;
  if (days === 1) return `ayer ${time}`;
  return `${date.getDate()} ${MONTHS[date.getMonth()]} ${time}`;
};

/** "4 min", "2 h 10 min", "3 d". */
export const durationLabel = (minutes: number): string => {
  const total = Math.max(0, Math.floor(minutes));
  if (total < 60) return `${total} min`;
  if (total < 24 * 60) {
    const rest = total % 60;
    return `${Math.floor(total / 60)} h${rest ? ` ${rest} min` : ""}`;
  }
  return `${Math.floor(total / (24 * 60))} d`;
};

/** Nobody has talked to the lead yet (it is still waiting for a call). */
export const isUncontacted = (lead: { stage: string }): boolean =>
  lead.stage === "new" || lead.stage === "no_answer";

export const minutesSince = (createdAt: string, now: number): number =>
  (now - new Date(createdAt).getTime()) / 60000;

/** Waiting colour: fine under 5 min, late under 1 hour, very late after. */
export const waitTone = (minutes: number): "ok" | "late" | "very_late" =>
  minutes < 5 ? "ok" : minutes < 60 ? "late" : "very_late";

/** "4:59" left of the 5-minute race, or how late it is ("+1:20"). */
export const raceClock = (
  createdAt: string,
  now: number,
): { label: string; late: boolean; secondsLeft: number } => {
  const elapsed = Math.floor((now - new Date(createdAt).getTime()) / 1000);
  const left = CALL_RACE_SECONDS - elapsed;
  const shown = Math.abs(left);
  const label = `${left < 0 ? "+" : ""}${Math.floor(shown / 60)}:${pad(shown % 60)}`;
  return { label, late: left < 0, secondsLeft: left };
};

export interface AlertLead {
  id: number | string;
  stage: string;
  sales_id?: number | string | null;
  source?: string | null;
  form_name?: string | null;
  created_at: string;
}

/** Leads that fill in a form: the ones the team has to call within 5 minutes. */
export const isFormLead = (lead: Pick<AlertLead, "source" | "form_name">) =>
  lead.source === "meta_form" || lead.source === "web" || !!lead.form_name;

/** The lead pops up for everybody: a free form lead in "Nuevo", just in. */
export const shouldAlert = (lead: AlertLead, now: number): boolean =>
  lead.stage === "new" &&
  lead.sales_id == null &&
  isFormLead(lead) &&
  minutesSince(lead.created_at, now) < ALERT_MAX_MINUTES;
